import { useEffect } from 'react'

import { MapPointGroup, ObjectDataRecord } from '@types'

import { normalizeLatLng } from '../lib/coordinates'
import { getPointLabel } from '../lib/labels'
import { getPointColor } from '../lib/pointStyle'
import { MapAPI, MarkerOptions } from '../lib/types'

import type { ObjectModelInfo } from './types'

/**
 * Хук для синхронизации маркеров на карте с данными точек
 * Использует PK-based ID вместо index для стабильной идентификации маркеров
 *
 * @param mapApi - API карты
 * @param isMapReady - флаг готовности карты
 * @param pointGroups - массив групп точек
 * @param data - загруженные данные точек
 * @param objectModelsMap - карта моделей объектов с PK полями
 */
export const useMapMarkers = (
  mapApi: MapAPI | null,
  isMapReady: boolean,
  pointGroups: MapPointGroup[],
  data: Map<string, ObjectDataRecord[]>,
  objectModelsMap: Map<string, ObjectModelInfo>
) => {
  useEffect(() => {
    // Ждём готовности карты, данных И моделей
    if (!isMapReady || !mapApi || data.size === 0 || objectModelsMap.size === 0) {
      return
    }

    // Полная синхронизация: очищаем все маркеры и добавляем заново по актуальным данным
    mapApi.clearMarkers()

    let totalMarkers = 0

    pointGroups.forEach(group => {
      const records = data.get(group.id)
      if (!records || records.length === 0) return

      // Получить информацию о модели для этой группы
      const modelInfo = objectModelsMap.get(group.objectCode)
      if (!modelInfo) {
        return
      }

      records.forEach(record => {
        const pkValue = record[modelInfo.pkFieldName]
        if (pkValue === undefined || pkValue === null) {
          return
        }

        const rawLat = record[group.coordinateFields.latitudeField]
        const rawLng = record[group.coordinateFields.longitudeField]
        const coords = normalizeLatLng(rawLat, rawLng)

        if (!coords) {
          return
        }

        const [lat, lng] = coords
        const markerId = `${group.id}-${pkValue}`

        const labelText = getPointLabel(group, record)
        const color = getPointColor(group, record)

        const markerOptions: MarkerOptions = {
          title: labelText,
        }

        if (color) {
          markerOptions.color = color
        }

        mapApi.addMarker(markerId, lat, lng, markerOptions)
        totalMarkers = totalMarkers + 1
      })
    })

    // Cleanup - удаляем все маркеры при размонтировании
    return () => {
      if (mapApi) {
        mapApi.clearMarkers()
      }
    }
  }, [isMapReady, mapApi, pointGroups, data, objectModelsMap])
}
